<template>
  <topHeader />
  <div class="tech-spec-detail">
    <div class="container">
      <!-- 面包屑导航 -->
      <div class="breadcrumb">
        <a @click="goToHome" class="breadcrumb-link">首页</a>
        <span class="breadcrumb-separator">></span>
        <a @click="goToTechSpecList" class="breadcrumb-link">技术规范</a>
        <span class="breadcrumb-separator">></span>
        <span class="breadcrumb-current">规范详情</span>
      </div>

      <!-- 加载状态 -->
      <div v-if="loading" class="loading-container">
        <div class="loading-text">加载中...</div>
      </div>

      <!-- 内容区域 -->
      <template v-else>
        <!-- 如果没有数据，显示提示 -->
        <div v-if="!currentSpec.id || !currentSpec.title" class="empty-container">
          <p>暂无数据</p>
        </div>
        
        <!-- 有数据时显示内容 -->
        <template v-else>
          <!-- 规范标题 -->
          <div class="article-title">
            <h1>{{ currentSpec.title }}</h1>
          </div>

          <!-- 规范元数据 -->
          <div class="article-meta">
            <span class="meta-date" v-if="currentSpec.date">发布时间：{{ currentSpec.date }}</span>
            <span class="meta-separator" v-if="currentSpec.source">|</span>
            <span class="meta-source" v-if="currentSpec.source">来源：{{ currentSpec.source }}</span>
            <span class="meta-separator" v-if="currentSpec.viewCount !== undefined">|</span>
            <span class="meta-views" v-if="currentSpec.viewCount !== undefined">浏览：{{ currentSpec.viewCount }}</span>
          </div>

          <!-- 附件预览 -->
          <div v-if="currentSpec.attachment" class="article-attachment">
            <a @click.prevent="previewAttachment" class="attachment-link" href="javascript:void(0)">
              <span class="iconfont icon-yulan"></span>
              附件：{{ currentSpec.attachmentName || getFileName(currentSpec.attachment) }}
            </a>
          </div>

          <!-- 规范正文 -->
          <div class="article-content">
            <div class="content-section" v-html="formatContent(currentSpec.content)"></div>
          </div>

          <!-- 底部导航 -->
          <div class="article-footer">
            <div class="footer-nav">
              <a 
                v-if="prevSpec" 
                @click="goToSpec(prevSpec.id)" 
                class="nav-link prev-link"
              >
                上一篇: {{ prevSpec.title }}
              </a>
              <span v-else class="nav-link prev-link disabled">上一篇: -</span>
              <a 
                v-if="nextSpec" 
                @click="goToSpec(nextSpec.id)" 
                class="nav-link next-link"
              >
                下一篇: {{ nextSpec.title }}
              </a>
              <span v-else class="nav-link next-link disabled">下一篇: -</span>
            </div>
          </div>
        </template>
      </template>
    </div>

    <!-- PDF预览弹窗 -->
    <div v-if="showPdfPreview" class="pdf-preview-modal" @click.self="closePdfPreview">
      <div class="pdf-preview-container">
        <div class="pdf-preview-header">
          <h3>{{ currentSpec.attachmentName || getFileName(currentSpec.attachment) }}</h3>
          <button class="close-btn" @click="closePdfPreview">×</button>
        </div>
        <div class="pdf-preview-content">
          <iframe 
            :src="pdfPreviewUrl" 
            class="pdf-iframe"
            frameborder="0"
          ></iframe>
        </div>
      </div>
    </div>
  </div>
  <Footer />
</template>

<script>
import TopHeader from './top-Header.vue'
import Footer from './Footer.vue'
import { getConsultInfoDetail, getConsultInfoList } from '@/api'

export default {
  name: 'techSpecDetail',
  components: {
    TopHeader,
    Footer
  },
  data() {
    return {
      currentSpec: {
        id: null,
        title: '',
        content: '',
        source: '',
        publishTime: '',
        createTime: '',
        viewCount: 0,
        attachment: null,
        attachmentName: null
      },
      loading: false,
      prevNews: null,
      nextNews: null,
      showPdfPreview: false,
      pdfPreviewUrl: '',
      prevSpec: null,
      nextSpec: null
    }
  },
  computed: {
    attachmentUrl() {
      if (!this.currentSpec.attachment) return ''
      const baseUrl = 'https://qtzp.qhdbigdata.com/qtzp-api/upload/'
      // 如果attachment已经是完整URL，直接返回，否则拼接
      return this.currentSpec.attachment.startsWith('http') 
        ? this.currentSpec.attachment 
        : baseUrl + this.currentSpec.attachment
    }
  },
  mounted() {
    this.loadSpecDetail()
    this.loadPrevNext()
  },
  methods: {
    goToHome() {
      this.$router.push('/');
    },
    goToTechSpecList() {
      this.$router.push('/techSpec');
    },
    
    // 加载上一篇和下一篇
    async loadPrevNext() {
      const id = this.$route.query.id || this.$route.params.id
      if (!id) return
      
      try {
        // 获取列表数据，用于查找上一篇和下一篇
        const params = {
          type: '2', // 2技术规范
          pageNum: '1',
          pageSize: '1000' // 获取足够多的数据
        }
        
        const res = await getConsultInfoList(params)
        const list = (res && res.rows) || res?.data?.rows || res?.data || []
        
        // 找到当前项在列表中的位置
        const currentIndex = list.findIndex(item => String(item.id) === String(id))
        
        if (currentIndex !== -1) {
          // 设置上一篇
          if (currentIndex > 0) {
            const prevItem = list[currentIndex - 1]
            this.prevSpec = {
              id: prevItem.id,
              title: prevItem.title || prevItem.name || '未命名'
            }
          } else {
            this.prevSpec = null
          }
          
          // 设置下一篇
          if (currentIndex < list.length - 1) {
            const nextItem = list[currentIndex + 1]
            this.nextSpec = {
              id: nextItem.id,
              title: nextItem.title || nextItem.name || '未命名'
            }
          } else {
            this.nextSpec = null
          }
        }
      } catch (error) {
        console.error('获取上一篇/下一篇失败:', error)
        this.prevSpec = null
        this.nextSpec = null
      }
    },
    
    // 跳转到其他技术规范
    goToSpec(id) {
      if (id) {
        this.$router.push({
          path: '/techSpecDetail',
          query: { id }
        }).then(() => {
          // 重新加载数据
          this.loadSpecDetail()
          this.loadPrevNext()
          // 滚动到顶部
          window.scrollTo({ top: 0, behavior: 'smooth' })
        })
      }
    },
    
    async loadSpecDetail() {
      // 从路由query参数获取id
      const id = this.$route.query.id || this.$route.params.id
      
      if (!id) {
        console.error('缺少id参数')
        this.$router.push('/techSpec')
        return
      }
      
      this.loading = true
      try {
        const res = await getConsultInfoDetail(id)
        
        // 处理返回数据
        const data = res?.data || res || {}
        
        // 检查数据是否为空
        if (!data || (!data.id && !data.title)) {
          console.warn('获取到的数据为空')
          this.currentSpec = {
            id: null,
            title: '',
            content: '',
            source: '',
            publishTime: '',
            createTime: '',
            viewCount: 0,
            attachment: null,
            attachmentName: null
          }
          this.loading = false
          return
        }
        
        this.currentSpec = {
          id: data.id || id,
          title: data.title || '未命名',
          content: data.content || data.detail || '',
          source: data.source || '',
          publishTime: data.publishTime || data.createTime || '',
          createTime: data.createTime || '',
          viewCount: data.viewCount || 0,
          attachment: data.attachment || null,
          attachmentName: data.attachmentName || null
        }
        
        // 格式化日期
        if (this.currentSpec.publishTime) {
          this.currentSpec.date = this.formatDate(this.currentSpec.publishTime)
        } else if (this.currentSpec.createTime) {
          this.currentSpec.date = this.formatDate(this.currentSpec.createTime)
        } else {
          this.currentSpec.date = ''
        }
      } catch (error) {
        console.error('获取技术规范详情失败:', error)
        // 清空数据
        this.currentSpec = {
          id: null,
          title: '',
          content: '',
          source: '',
          publishTime: '',
          createTime: '',
          viewCount: 0,
          attachment: null,
          attachmentName: null
        }
        // 显示错误提示
        alert('获取技术规范详情失败，请稍后重试')
        setTimeout(() => {
          this.$router.push('/techSpec')
        }, 2000)
      } finally {
        this.loading = false
      }
    },
    
    // 格式化日期
    formatDate(dateString) {
      if (!dateString) return ''
      const date = new Date(dateString)
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      return `${year}-${month}-${day}`
    },
    
    // 格式化内容（处理HTML和换行）
    formatContent(content) {
      if (!content) return ''
      // 如果内容包含HTML标签，直接返回
      if (content.includes('<') && content.includes('>')) {
        return content
      }
      // 否则将换行符转换为<br>
      return content.replace(/\n/g, '<br>')
    },
    
    // 预览附件
    previewAttachment() {
      if (!this.currentSpec.attachment) return
      this.pdfPreviewUrl = this.attachmentUrl
      this.showPdfPreview = true
    },
    
    // 关闭PDF预览
    closePdfPreview() {
      this.showPdfPreview = false
      this.pdfPreviewUrl = ''
    },
    
    // 从文件路径提取文件名
    getFileName(filePath) {
      if (!filePath) return '附件'
      // 如果是完整URL，提取路径部分
      let path = filePath
      try {
        const url = new URL(filePath)
        path = url.pathname
      } catch (e) {
        // 如果不是URL，直接使用
      }
      // 提取文件名
      const fileName = path.split('/').pop() || path.split('\\').pop() || '附件'
      return fileName
    }
  }
}
</script>

<style scoped>
.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
  background: #f5f5f5;
  min-height: 100vh;
}

/* 面包屑导航 */
.breadcrumb {
  margin-bottom: 20px;
  font-size: 14px;
  color: #666;
  text-align: left;
}

.breadcrumb-link {
  color: #422C27;
  text-decoration: none;
  margin: 0 5px;
  cursor: pointer;
  transition: color 0.3s;
}

.breadcrumb-link:hover {
  text-decoration: underline;
  color: #5C3D2E;
}

.breadcrumb-separator {
  margin: 0 5px;
  color: #999;
}

.breadcrumb-current {
  color: #333;
  margin-left: 5px;
  font-weight: 500;
}

/* 规范标题 */
.article-title {
  text-align: center;
  margin-bottom: 20px;
}

.article-title h1 {
  font-size: 32px;
  font-weight: bold;
  color: #333;
  margin: 0;
  line-height: 1.4;
}

/* 规范元数据 */
.article-meta {
  text-align: center;
  margin-bottom: 30px;
  font-size: 14px;
  color: #666;
  padding-bottom: 15px;
  border-bottom: 1px solid #e0e0e0;
}

.meta-date,
.meta-source,
.meta-views {
  color: #666;
}

.meta-separator {
  margin: 0 10px;
  color: #999;
}

/* 规范正文 */
.article-content {
  background: white;
  padding: 30px;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  margin-bottom: 30px;
  text-align: left;
}

.content-section {
  margin-bottom: 20px;
  line-height: 2;
  font-size: 16px;
  color: #333;
  text-indent: 2em;
}

.content-section:last-child {
  margin-bottom: 0;
}

/* 底部导航 */
.article-footer {
  background: white;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
}

.footer-nav {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.nav-link {
  color: #422C27;
  text-decoration: none;
  font-size: 14px;
  padding: 8px 15px;
  border: 1px solid #422C27;
  border-radius: 4px;
  transition: all 0.3s;
}

.nav-link:hover {
  background: #422C27;
  color: white;
}

.prev-link {
  margin-right: auto;
}

.next-link {
  margin-left: auto;
}

/* 加载状态 */
.loading-container {
  text-align: center;
  padding: 60px 20px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
}

.loading-text {
  font-size: 16px;
  color: #666;
}

/* 附件下载 */
.article-attachment {
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  padding: 15px 20px;
  margin-bottom: 30px;
}

.attachment-link {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: #422C27;
  text-decoration: none;
  font-size: 14px;
  transition: color 0.3s ease;
}

.attachment-link:hover {
  color: #E4CEC2;
  text-decoration: underline;
}

.nav-link.disabled {
  color: #999;
  cursor: not-allowed;
  border-color: #ddd;
}

.nav-link.disabled:hover {
  background: transparent;
  color: #999;
}

/* PDF预览弹窗 */
.pdf-preview-modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.8);
  z-index: 2000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
}

.pdf-preview-container {
  background: white;
  border-radius: 8px;
  width: 90%;
  max-width: 1200px;
  height: 90vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.5);
}

.pdf-preview-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px 20px;
  border-bottom: 1px solid #e0e0e0;
  background: #f8f9fa;
  flex-shrink: 0;
}

.pdf-preview-header h3 {
  margin: 0;
  font-size: 16px;
  color: #333;
  font-weight: 500;
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.pdf-preview-header .close-btn {
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #666;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: background-color 0.3s;
  flex-shrink: 0;
  margin-left: 15px;
}

.pdf-preview-header .close-btn:hover {
  background: #eee;
}

.pdf-preview-content {
  flex: 1;
  overflow: hidden;
  position: relative;
}

.pdf-iframe {
  width: 100%;
  height: 100%;
  border: none;
}

@media (max-width: 768px) {
  .pdf-preview-container {
    width: 95%;
    height: 95vh;
  }
  
  .pdf-preview-header {
    padding: 12px 15px;
  }
  
  .pdf-preview-header h3 {
    font-size: 14px;
  }
}

/* 响应式设计 */
@media (max-width: 768px) {
  .container {
    padding: 15px;
  }
  
  .article-title h1 {
    font-size: 24px;
  }
  
  .article-content {
    padding: 20px;
  }
  
  .content-section {
    font-size: 14px;
    line-height: 1.8;
  }
  
  .footer-nav {
    flex-direction: column;
    gap: 10px;
  }
  
  .prev-link,
  .next-link {
    margin: 0;
    width: 100%;
    text-align: center;
  }
}

@media (max-width: 480px) {
  .article-title h1 {
    font-size: 20px;
  }
  
  .article-meta {
    font-size: 12px;
  }
  
  .breadcrumb {
    font-size: 12px;
  }
}

/* 空状态 */
.empty-container {
  text-align: center;
  padding: 60px 20px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
}

.empty-container p {
  font-size: 16px;
  color: #999;
  margin: 0;
}
</style>

